import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink, ChevronLeft, ChevronRight, X, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { usePrivacy, ConsentLevel, getAdSenseConfig } from './PrivacyConsent';
import { useAdVisibility } from '../hooks/useAdVisibility';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Google AdSense Configuration
 * NOTE: AdSense scripts are ONLY loaded after user consent (GDPR compliant)
 */
const ADSENSE_CONFIG = {
  clientId: 'ca-pub-8607565924578094',
  enabled: true
};

/**
 * Google Ad Manager Configuration
 * Loaded from backend API - falls back to defaults if not configured
 */
let GAM_CONFIG = {
  networkCode: '',
  adUnits: {
    homepage_banner: '',
    sidebar_rectangle: '',
    in_feed: '',
    footer_banner: ''
  },
  sizes: {
    homepage_banner: [[970, 90], [728, 90], [320, 50]],
    sidebar_rectangle: [[300, 250], [300, 600]],
    in_feed: [[300, 250], [336, 280]],
    footer_banner: [[728, 90], [320, 50]]
  },
  enabled: false,
  loaded: false
};

// Load GAM config from backend on module load
const loadGAMConfig = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/admin/ads/config/public`);
    if (response.data) {
      GAM_CONFIG = {
        ...GAM_CONFIG,
        networkCode: response.data.network_code || '',
        adUnits: response.data.ad_units || GAM_CONFIG.adUnits,
        sizes: response.data.ad_sizes || GAM_CONFIG.sizes,
        enabled: response.data.enabled || false,
        loaded: true
      };
    }
  } catch (error) {
    console.log('GAM config not available, using defaults');
    GAM_CONFIG.loaded = true;
  }
};

// Initialize on load
loadGAMConfig();

/**
 * Load AdSense script with privacy controls (GDPR compliant)
 * Only loads if user has given consent
 */
const loadAdSenseScript = (npa = '1') => {
  // Check consent before loading
  const consent = localStorage.getItem('aadc_privacy_consent');
  const allowsAds = consent === 'limited' || consent === 'full';
  
  if (!allowsAds) {
    console.log('AdSense: Not loading - no consent');
    return;
  }
  
  if (document.querySelector(`script[src*="adsbygoogle"]`)) return;
  
  const script = document.createElement('script');
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CONFIG.clientId}`;
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.dataset.consentScript = 'advertising';
  
  // Set non-personalized ads flag before script loads
  window.adsbygoogle = window.adsbygoogle || [];
  if (npa === '1') {
    window.adsbygoogle.requestNonPersonalizedAds = 1;
  }
  
  document.head.appendChild(script);
};

/**
 * AdSense Display Ad Component
 * Simple, auto-sized display ad using Google AdSense
 * Respects user privacy consent (GDPR compliant)
 * Will NOT render or load scripts until user has given consent
 */
export const AdSenseAd = ({ 
  slot = '',
  format = 'auto',
  responsive = true,
  className = '',
  style = {}
}) => {
  const [loaded, setLoaded] = useState(false);
  const adRef = useRef(null);
  
  // Get privacy consent
  const { consentLevel, hasConsented, allowsAnyAds } = usePrivacy();
  const adConfig = getAdSenseConfig(consentLevel);

  useEffect(() => {
    // GDPR: Don't load if no consent or user opted out
    if (!ADSENSE_CONFIG.enabled || !adConfig.enabled || !hasConsented || !allowsAnyAds) {
      return;
    }
    
    loadAdSenseScript(adConfig.npa);
    
    const timer = setTimeout(() => {
      try {
        if (window.adsbygoogle && adRef.current) {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          setLoaded(true);
        }
      } catch (e) {
        console.error('AdSense error:', e);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [adConfig.enabled, adConfig.npa, hasConsented, allowsAnyAds]);

  // GDPR: Don't render anything if user hasn't consented
  if (!ADSENSE_CONFIG.enabled || !adConfig.enabled || !hasConsented || !allowsAnyAds) {
    return null;
  }

  return (
    <div className={`adsense-container ${className}`} style={style}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', minHeight: '90px', ...style }}
        data-ad-client={ADSENSE_CONFIG.clientId}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
      />
      {!loaded && (
        <div className="text-xs text-center text-muted-foreground py-4 bg-muted/30">
          Advertisement
        </div>
      )}
    </div>
  );
};

/**
 * Ad Source Types
 */
export const AdSourceType = {
  GOOGLE: 'google',      // Google Ad Manager
  PARTNER: 'partner',    // Internal partner/sponsor ads
  PROMO: 'promo',        // Platform promotional content
  MEMBER: 'member'       // Pro/Premium member ads
};

/**
 * Google Ad Manager Component
 * Renders a GAM ad slot
 */
export const GoogleAd = ({ 
  adUnit, 
  size = 'responsive',
  className = '',
  fallback = null 
}) => {
  const adRef = useRef(null);
  const [adLoaded, setAdLoaded] = useState(false);
  const [adError, setAdError] = useState(false);

  useEffect(() => {
    if (!GAM_CONFIG.enabled) {
      setAdError(true);
      return;
    }

    // Load GPT library if not already loaded
    const loadGPT = () => {
      return new Promise((resolve) => {
        if (window.googletag && window.googletag.apiReady) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
        script.async = true;
        script.onload = () => {
          window.googletag = window.googletag || { cmd: [] };
          window.googletag.cmd.push(() => {
            window.googletag.pubads().enableSingleRequest();
            window.googletag.enableServices();
            resolve();
          });
        };
        script.onerror = () => setAdError(true);
        document.head.appendChild(script);
      });
    };

    const initAd = async () => {
      try {
        await loadGPT();
        
        const adUnitPath = GAM_CONFIG.adUnits[adUnit];
        const adSizes = GAM_CONFIG.sizes[adUnit] || [[300, 250]];
        
        if (!adUnitPath || !adRef.current) return;

        const slotId = `gam-${adUnit}-${Date.now()}`;
        adRef.current.id = slotId;

        window.googletag.cmd.push(() => {
          const slot = window.googletag.defineSlot(adUnitPath, adSizes, slotId);
          if (slot) {
            slot.addService(window.googletag.pubads());
            window.googletag.display(slotId);
            setAdLoaded(true);
          }
        });
      } catch (error) {
        console.error('GAM ad load error:', error);
        setAdError(true);
      }
    };

    initAd();
  }, [adUnit]);

  // Show fallback if ad fails or GAM is disabled
  if (adError && fallback) {
    return fallback;
  }

  if (adError) {
    return null;
  }

  return (
    <div 
      ref={adRef}
      className={`gam-ad-container ${className}`}
      data-testid={`google-ad-${adUnit}`}
      style={{ minHeight: adLoaded ? 'auto' : '90px' }}
    >
      {!adLoaded && (
        <div className="flex items-center justify-center h-full bg-muted/30 animate-pulse">
          <span className="text-xs text-muted-foreground">Loading ad...</span>
        </div>
      )}
    </div>
  );
};

/**
 * Mixed Ad Slot Component
 * Intelligently serves ads from different sources based on priority, availability, and member class
 * - FREE members: See all ads (Google + sponsors)
 * - PAID members: See sponsor ads only (no Google)
 * - PREMIUM members: No ads
 */
export const MixedAdSlot = ({
  placement = 'homepage',
  sources = [AdSourceType.PARTNER, AdSourceType.GOOGLE],
  googleAdUnit = 'homepage_banner',
  className = '',
  showLabel = true,
  userMemberClass = null  // Pass user's member_class to control ad visibility
}) => {
  const [partnerAd, setPartnerAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [useGoogle, setUseGoogle] = useState(false);

  // Determine what ads to show based on member class
  const canSeeGoogleAds = !userMemberClass || userMemberClass === 'free' || userMemberClass === 'guest';
  const canSeeSponsorAds = !userMemberClass || ['free', 'guest', 'paid', 'volunteer'].includes(userMemberClass);
  const canSeeAnyAds = userMemberClass !== 'premium';

  useEffect(() => {
    // Premium members see no ads
    if (!canSeeAnyAds) {
      setLoading(false);
      return;
    }
    fetchPartnerAd();
  }, [placement, canSeeAnyAds]);

  const fetchPartnerAd = async () => {
    try {
      // Try to fetch a partner/internal ad first if member can see sponsor ads
      if (canSeeSponsorAds) {
        const response = await axios.get(`${API_URL}/api/banners/${placement}?limit=1`);
        if (response.data && response.data.length > 0) {
          setPartnerAd(response.data[0]);
          setLoading(false);
          return;
        }
      }
      
      // Fall back to Google if allowed and no partner ads
      if (canSeeGoogleAds && sources.includes(AdSourceType.GOOGLE) && GAM_CONFIG.enabled) {
        setUseGoogle(true);
      }
    } catch (error) {
      console.error('Error fetching partner ad:', error);
      if (canSeeGoogleAds && sources.includes(AdSourceType.GOOGLE) && GAM_CONFIG.enabled) {
        setUseGoogle(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    if (!partnerAd) return;
    
    try {
      await axios.post(`${API_URL}/api/banners/${partnerAd.id}/click`);
    } catch (error) {
      console.error('Error tracking click:', error);
    }
    
    if (partnerAd.link_url.startsWith('/')) {
      window.location.href = partnerAd.link_url;
    } else {
      window.open(partnerAd.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Premium members see no ads
  if (!canSeeAnyAds) return null;
  if (dismissed) return null;
  if (loading) {
    return (
      <div className={`animate-pulse bg-muted/30 h-24 rounded-lg ${className}`} />
    );
  }

  // Use Google Ad if no partner ad and Google is enabled and allowed
  if (useGoogle && canSeeGoogleAds) {
    return (
      <div className={`relative ${className}`} data-testid="mixed-ad-google">
        <GoogleAd adUnit={googleAdUnit} />
        {showLabel && (
          <span className="absolute bottom-1 right-2 text-[10px] text-muted-foreground">
            Advertisement
          </span>
        )}
      </div>
    );
  }

  // No ads available
  if (!partnerAd) return null;

  // Render partner/internal ad
  return (
    <div 
      className={`relative overflow-hidden rounded-lg cursor-pointer transition-all hover:shadow-lg ${className}`}
      style={{ 
        backgroundColor: partnerAd.background_color || '#1e40af',
        color: partnerAd.text_color || '#ffffff'
      }}
      onClick={handleClick}
      data-testid="mixed-ad-partner"
    >
      {/* Dismiss button */}
      <button
        onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
        className="absolute top-2 right-2 z-10 p-1 rounded-full opacity-60 hover:opacity-100 transition-opacity"
        style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
        aria-label="Dismiss ad"
      >
        <X className="w-3 h-3" />
      </button>

      {/* Ad Content */}
      {partnerAd.banner_type === 'image' && partnerAd.image_url ? (
        <img 
          src={partnerAd.image_url} 
          alt={partnerAd.title}
          className="w-full h-auto"
        />
      ) : (
        <div className="p-4">
          {partnerAd.partnership_name && (
            <span className="text-[10px] uppercase tracking-wider opacity-75 mb-1 block">
              Partner: {partnerAd.partnership_name}
            </span>
          )}
          <h3 className="font-bold text-lg mb-1">{partnerAd.title}</h3>
          {partnerAd.description && (
            <p className="text-sm opacity-90 mb-3 line-clamp-2">
              {partnerAd.description}
            </p>
          )}
          <div className="flex items-center justify-between">
            <Button size="sm" variant="secondary" className="pointer-events-none">
              {partnerAd.cta_text || 'Learn More'}
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Ad label */}
      {showLabel && (
        <span className="absolute bottom-1 right-2 text-[10px] opacity-50">
          {partnerAd.partnership_name ? 'Sponsored' : 'Ad'}
        </span>
      )}
    </div>
  );
};

/**
 * Homepage Ad Banner - Primary above-the-fold placement
 * Rotates between partner ads and Google ads
 * Respects member class for ad visibility:
 * - GUEST (not logged in): Google AdSense + Sponsor ads
 * - FREE/PAID/VOLUNTEER: Sponsor ads only (no Google during beta)
 * - PREMIUM: No ads
 */
export const HomepageAdBanner = ({ userMemberClass = null, clubId = null }) => {
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [showAdSense, setShowAdSense] = useState(false);
  
  // Use the ad visibility hook for club-aware ad decisions
  const { shouldShowAds, shouldShowSponsorAds, shouldShowGoogleAds, loading: policyLoading } = useAdVisibility(clubId);

  // Determine what ads to show based on member class and club policy
  const isGuest = !userMemberClass || userMemberClass === 'guest';
  const canSeeGoogleAds = shouldShowGoogleAds && ADSENSE_CONFIG.enabled;
  const canSeeSponsorAds = shouldShowSponsorAds;
  const canSeeAnyAds = shouldShowAds;

  useEffect(() => {
    if (!canSeeAnyAds) {
      setLoading(false);
      return;
    }
    fetchAds();
  }, [canSeeAnyAds]);

  // Auto-rotate every 8 seconds
  useEffect(() => {
    if (ads.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % ads.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [ads.length]);

  const fetchAds = async () => {
    try {
      let allAds = [];
      
      // Fetch sponsor/partner ads if allowed
      if (canSeeSponsorAds) {
        const response = await axios.get(`${API_URL}/api/banners/homepage?limit=5`);
        allAds = response.data.map(ad => ({ ...ad, source: AdSourceType.PARTNER }));
      }
      
      // If no partner ads and guest can see Google ads, show AdSense
      if (allAds.length === 0 && canSeeGoogleAds) {
        setShowAdSense(true);
      }
      
      setAds(allAds);
    } catch (error) {
      console.error('Error fetching homepage ads:', error);
      // Fall back to AdSense for guests if partner ads fail
      if (canSeeGoogleAds) {
        setShowAdSense(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (ad) => {
    if (ad.source === AdSourceType.GOOGLE) return;
    
    try {
      await axios.post(`${API_URL}/api/banners/${ad.id}/click`);
    } catch (error) {
      console.error('Error tracking click:', error);
    }
    
    if (ad.link_url.startsWith('/')) {
      window.location.href = ad.link_url;
    } else {
      window.open(ad.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Premium members see no ads
  if (!canSeeAnyAds) return null;
  if (dismissed) return null;
  
  // Show AdSense for guests when no partner ads available
  if (showAdSense && canSeeGoogleAds) {
    return (
      <div className="relative w-full bg-muted/30" data-testid="homepage-adsense-banner">
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-2 right-2 z-10 p-1 rounded-full bg-black/20 opacity-60 hover:opacity-100"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-white" />
        </button>
        <div className="container mx-auto px-4 py-2">
          <AdSenseAd format="horizontal" className="mx-auto" />
        </div>
        <div className="absolute bottom-1 right-2 text-[10px] text-muted-foreground">
          Advertisement
        </div>
      </div>
    );
  }
  
  if (loading || ads.length === 0) return null;

  const currentAd = ads[currentIndex];

  // Render Google Ad (only if allowed)
  if (currentAd.source === AdSourceType.GOOGLE && canSeeGoogleAds) {
    return (
      <div className="relative w-full bg-muted" data-testid="homepage-ad-banner-google">
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-2 right-2 z-10 p-1 rounded-full bg-black/20 opacity-60 hover:opacity-100"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-white" />
        </button>
        <div className="container mx-auto px-4 py-2">
          <GoogleAd adUnit={currentAd.adUnit} className="mx-auto" />
        </div>
        <AdNavigation 
          ads={ads} 
          currentIndex={currentIndex} 
          setCurrentIndex={setCurrentIndex} 
        />
      </div>
    );
  }

  // Render Partner Ad
  return (
    <div 
      data-testid="homepage-ad-banner"
      className="relative w-full overflow-hidden cursor-pointer"
      style={{ 
        backgroundColor: currentAd.background_color || '#1e40af',
        color: currentAd.text_color || '#ffffff'
      }}
      onClick={() => handleClick(currentAd)}
    >
      <button
        onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
        className="absolute top-2 right-2 z-10 p-1 rounded-full opacity-60 hover:opacity-100 transition-opacity"
        style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="container mx-auto px-4 py-4 md:py-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex-1 text-center md:text-left">
            {currentAd.partnership_name && (
              <span className="text-xs uppercase tracking-wider opacity-75 mb-1 block">
                Partner: {currentAd.partnership_name}
              </span>
            )}
            <h3 className="text-lg md:text-xl font-bold mb-1">{currentAd.title}</h3>
            {currentAd.description && (
              <p className="text-sm opacity-90 max-w-2xl">{currentAd.description}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="secondary" className="whitespace-nowrap font-medium pointer-events-none">
              {currentAd.cta_text || 'Learn More'}
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
            {currentAd.secondary_cta_text && (
              <Button 
                variant="ghost" 
                className="whitespace-nowrap font-medium text-white/90 hover:text-white hover:bg-white/20"
                onClick={(e) => {
                  e.stopPropagation();
                  if (currentAd.secondary_link_url) {
                    window.location.href = currentAd.secondary_link_url;
                  }
                }}
              >
                {currentAd.secondary_cta_text}
              </Button>
            )}
          </div>
        </div>
      </div>

      <AdNavigation 
        ads={ads} 
        currentIndex={currentIndex} 
        setCurrentIndex={setCurrentIndex}
        bgColor={currentAd.background_color}
      />

      <div className="absolute bottom-2 right-2 text-xs opacity-50">
        Sponsored
      </div>
    </div>
  );
};

/**
 * Ad Navigation Component (dots and arrows)
 */
const AdNavigation = ({ ads, currentIndex, setCurrentIndex, bgColor }) => {
  if (ads.length <= 1) return null;

  return (
    <>
      <button
        onClick={(e) => { 
          e.stopPropagation(); 
          setCurrentIndex((prev) => (prev - 1 + ads.length) % ads.length);
        }}
        className="absolute left-2 top-1/2 -translate-y-1/2 p-1 rounded-full opacity-60 hover:opacity-100 hidden md:block"
        style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
      >
        <ChevronLeft className="w-5 h-5 text-white" />
      </button>
      <button
        onClick={(e) => { 
          e.stopPropagation(); 
          setCurrentIndex((prev) => (prev + 1) % ads.length);
        }}
        className="absolute right-8 top-1/2 -translate-y-1/2 p-1 rounded-full opacity-60 hover:opacity-100 hidden md:block"
        style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
      >
        <ChevronRight className="w-5 h-5 text-white" />
      </button>
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
        {ads.map((ad, index) => (
          <button
            key={ad.id}
            onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentIndex 
                ? 'bg-white w-4' 
                : 'bg-white/50 hover:bg-white/75'
            }`}
            aria-label={`Go to ad ${index + 1}`}
          />
        ))}
      </div>
    </>
  );
};

/**
 * Sidebar Ad Stack - Multiple ads for sidebar placement
 * Respects club ad policy settings
 */
export const SidebarAdStack = ({ className = '', maxAds = 3, clubId = null }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const { shouldShowAds, shouldShowSponsorAds, loading: policyLoading } = useAdVisibility(clubId);

  useEffect(() => {
    if (policyLoading) return;
    if (!shouldShowAds) {
      setLoading(false);
      return;
    }
    fetchAds();
  }, [shouldShowAds, policyLoading]);

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/banners/sidebar?limit=${maxAds}`);
      let partnerAds = [];
      
      if (shouldShowSponsorAds) {
        partnerAds = response.data.map(ad => ({ ...ad, source: AdSourceType.PARTNER }));
      }
      
      // Add Google ad to the mix if enabled and we have room
      if (GAM_CONFIG.enabled && partnerAds.length < maxAds) {
        partnerAds.splice(1, 0, { 
          id: 'google-sidebar', 
          source: AdSourceType.GOOGLE,
          adUnit: 'sidebar_rectangle'
        });
      }
      
      setAds(partnerAds.slice(0, maxAds));
    } catch (error) {
      console.error('Error fetching sidebar ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (ad) => {
    if (ad.source === AdSourceType.GOOGLE) return;
    
    try {
      await axios.post(`${API_URL}/api/banners/${ad.id}/click`);
    } catch (error) {
      console.error('Error tracking click:', error);
    }
    
    if (ad.link_url.startsWith('/')) {
      window.location.href = ad.link_url;
    } else {
      window.open(ad.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading || ads.length === 0) return null;

  return (
    <div className={`space-y-4 ${className}`} data-testid="sidebar-ad-stack">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
        <Megaphone className="w-4 h-4" />
        Offers & Partners
      </h4>
      
      {ads.map((ad) => (
        ad.source === AdSourceType.GOOGLE ? (
          <div key={ad.id} className="relative">
            <GoogleAd adUnit={ad.adUnit} className="rounded-lg overflow-hidden" />
            <span className="absolute bottom-1 right-2 text-[10px] text-muted-foreground">Ad</span>
          </div>
        ) : (
          <div
            key={ad.id}
            className="rounded-lg overflow-hidden cursor-pointer transition-transform hover:scale-[1.02] hover:shadow-lg"
            style={{ 
              backgroundColor: ad.background_color || '#1e40af',
              color: ad.text_color || '#ffffff'
            }}
            onClick={() => handleClick(ad)}
          >
            {ad.banner_type === 'image' && ad.image_url ? (
              <img src={ad.image_url} alt={ad.title} className="w-full h-auto" />
            ) : (
              <div className="p-4">
                <h5 className="font-semibold text-sm mb-1">{ad.title}</h5>
                {ad.description && (
                  <p className="text-xs opacity-90 mb-3 line-clamp-2">{ad.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium opacity-75">
                    {ad.cta_text || 'Learn More'} →
                  </span>
                  <span className="text-[10px] opacity-50">
                    {ad.partnership_name ? 'Sponsored' : 'Ad'}
                  </span>
                </div>
              </div>
            )}
          </div>
        )
      ))}
    </div>
  );
};

/**
 * In-Feed Ad Component - For inserting ads between content items
 */
export const InFeedAd = ({ className = '' }) => {
  return (
    <MixedAdSlot
      placement="sidebar"
      sources={[AdSourceType.PARTNER, AdSourceType.GOOGLE]}
      googleAdUnit="in_feed"
      className={`my-4 ${className}`}
      showLabel={true}
    />
  );
};

/**
 * Ad Configuration Utility
 * Use this to update GAM settings at runtime
 */
export const configureGoogleAds = (config) => {
  Object.assign(GAM_CONFIG, config);
};

/**
 * Check if Google Ads are enabled
 */
export const isGoogleAdsEnabled = () => GAM_CONFIG.enabled;

/**
 * Check if AdSense is enabled
 */
export const isAdSenseEnabled = () => ADSENSE_CONFIG.enabled;

export default HomepageAdBanner;
